import chalk from 'chalk';
import fs from 'fs';
import os from 'os';
import path from 'path';

import { ExpoRunFormatter } from './ExpoRunFormatter';

export function formatXcodeBuildPipeProcessAsync(
  projectRoot: string,
  { xcodeProjectName }: { xcodeProjectName?: string } = {}
) {
  return new Promise<string>(async (resolve, reject) => {
    const hooks = createXcodeBuildHooks(projectRoot, { xcodeProjectName, resolve, reject });

    process.stdin.on('data', hooks.onData);
    process.stdin.on('end', () => {
      hooks.onEnd(0);
    });
  });
}

export function createXcodeBuildHooks(
  projectRoot: string,
  {
    xcodeProjectName,
    resolve,
    reject,
  }: {
    xcodeProjectName?: string;
    resolve: (buildOutput: string) => void;
    reject: (error: Error) => void;
  }
) {
  const formatter = ExpoRunFormatter.create(projectRoot, {
    xcodeProject: xcodeProjectName ? { name: xcodeProjectName } : undefined,
    isDebug: isTruthy(process.env.EXPO_DEBUG),
  });

  let buildOutput = '';
  let errorOutput = '';

  let currentBuffer = '';

  // Data can be sent in chunks that would have no relevance to our regex
  // this can cause massive slowdowns, so we need to ensure the data is complete before attempting to parse it.
  function flushBuffer() {
    if (!currentBuffer) {
      return;
    }

    const data = currentBuffer;
    currentBuffer = '';
    const lines = formatter.pipe(data);
    for (const line of lines) {
      console.log(line);
    }
  }

  const onData = (data: Buffer) => {
    const stringData = data.toString();
    buildOutput += stringData;
    currentBuffer += stringData;
    if (currentBuffer.endsWith(os.EOL)) {
      flushBuffer();
    }
  };

  const onErr = (data: Buffer) => {
    flushBuffer();
    const stringData = data instanceof Buffer ? data.toString() : data;
    errorOutput += stringData;
  };

  const onEnd = (code: number) => {
    flushBuffer();
    console.log(formatter.getBuildSummary());
    const logFilePath = writeBuildLogs(projectRoot, buildOutput, errorOutput);
    if (code !== 0) {
      // Determine if the logger found any errors;
      const wasErrorPresented = !!formatter.errors.length;

      const errorTitle = `Failed to build iOS project. "xcodebuild" exited with error code ${code}.`;

      if (wasErrorPresented) {
        // This has a flaw, if the user is missing a file, and there is a script error, only the missing file error will be shown.
        // They will only see the script error if they fix the missing file and rerun.
        // The flaw can be fixed by catching script errors in the custom logger.
        reject(new Error(errorTitle));
        return;
      }

      // Show all the log info because often times the error is coming from a shell script,
      // that invoked a node script, that started metro, which threw an error.
      reject(
        new Error(
          `${errorTitle}\nTo view more error logs, try building the app with Xcode directly, by opening ${'unknown'}.\n\n` +
            buildOutput +
            '\n\n' +
            errorOutput +
            `Build logs written to ${chalk.underline(logFilePath)}`
        )
      );
      return;
    }
    resolve(buildOutput);
  };

  return { onData, onErr, onEnd };
}

export function writeBuildLogs(projectRoot: string, buildOutput: string, errorOutput: string) {
  const [logFilePath, errorFilePath] = getErrorLogFilePath(projectRoot);

  fs.writeFileSync(logFilePath, buildOutput);
  fs.writeFileSync(errorFilePath, errorOutput);
  return logFilePath;
}

export function getErrorLogFilePath(projectRoot: string): [string, string] {
  const folder = path.join(projectRoot, '.expo');
  fs.mkdirSync(folder, { recursive: true });
  return [path.join(folder, 'xcodebuild.log'), path.join(folder, 'xcodebuild-error.log')];
}

function isTruthy(value: any): boolean {
  const str = String(value).toLowerCase();
  return str === 'true' || str === '1';
}
